import { lazy, Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/Layout/AppShell';
import { PlatformAppShell } from '@/components/Layout/PlatformAppShell';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireRole } from '@/components/auth/RequireRole';
// Public entry pages stay eager: they are the critical path for first paint
// (landing SEO / login), and they are small.
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';

// ---------------------------------------------------------------------------
// Code splitting: every authenticated page loads as its own chunk so the
// public landing/login bundle stays small (Core Web Vitals / SEO).
// ---------------------------------------------------------------------------

function PageLoader() {
  return (
    <div className="flex h-full min-h-[40vh] items-center justify-center" aria-busy="true">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
    </div>
  );
}

/**
 * A deploy replaces the hashed chunk files, so a tab that was already open
 * asks for a file that no longer exists and the page dies with "Failed to
 * fetch dynamically imported module". Reload once to pick up the fresh
 * index.html; the guard keeps a genuinely broken chunk from looping.
 */
const RELOAD_GUARD_KEY = 'chunk-reload-at';

async function loadPageModule(
  load: () => Promise<Record<string, unknown>>,
  name: string,
): Promise<{ default: ComponentType }> {
  try {
    const mod = await load();
    return { default: mod[name] as ComponentType };
  } catch (err) {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
      // The page is going away; never resolve so nothing renders meanwhile.
      return new Promise<never>(() => {});
    }
    throw err;
  }
}

/** Wrap a named-export page in React.lazy + Suspense. */
function lazyPage(
  load: () => Promise<Record<string, unknown>>,
  name: string,
): ReactElement {
  const C = lazy(() => loadPageModule(load, name));
  return (
    <Suspense fallback={<PageLoader />}>
      <C />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Public landing at the bare root. The authenticated tenant tree below has
  // no index route, so "/" resolves here; LandingPage itself redirects
  // logged-in users to /dashboard.
  { path: '/', element: <LandingPage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },

  // Platform admin route tree — separate shell, role-gated
  {
    path: '/platform',
    element: (
      <RequireAuth>
        <RequireRole roles={['super_admin', 'platform_admin']}>
          <PlatformAppShell />
        </RequireRole>
      </RequireAuth>
    ),
    children: [
      { index: true, element: lazyPage(() => import('@/pages/platform/OverviewPage'), 'OverviewPage') },
      { path: 'companies', element: lazyPage(() => import('@/pages/platform/CompaniesPage'), 'CompaniesPage') },
      { path: 'companies/:id/employees', element: lazyPage(() => import('@/pages/platform/CompanyEmployeesPage'), 'CompanyEmployeesPage') },
      { path: 'orders', element: lazyPage(() => import('@/pages/platform/OrdersPage'), 'OrdersPage') },
      { path: 'telegram-chats', element: lazyPage(() => import('@/pages/platform/TelegramChatsPage'), 'TelegramChatsPage') },
      { path: 'devices', element: lazyPage(() => import('@/pages/platform/DevicesPage'), 'PlatformDevicesPage') },
      { path: 'users', element: lazyPage(() => import('@/pages/platform/UsersPage'), 'PlatformUsersPage') },
      { path: 'billing', element: lazyPage(() => import('@/pages/platform/BillingPage'), 'BillingPage') },
      { path: 'billing/companies/:id', element: lazyPage(() => import('@/pages/platform/CompanyBillingDetailPage'), 'CompanyBillingDetailPage') },
      { path: 'invoices', element: lazyPage(() => import('@/pages/platform/InvoicesPage'), 'InvoicesPage') },
    ],
  },

  // Tenant route tree — AppShell redirects platform_admin away at render time
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // NOTE: no index route — the bare "/" belongs to the public landing
      // page above. Logged-in visitors are forwarded to /dashboard by it.
      { path: 'dashboard', element: lazyPage(() => import('@/pages/DashboardPage'), 'DashboardPage') },
      {
        path: 'dashboard/employees/:id',
        element: lazyPage(() => import('@/pages/DashboardEmployeePage'), 'DashboardEmployeePage'),
      },
      {
        path: 'reports',
        element: (
          <RequireRole roles={['admin', 'super_admin']}>
            {lazyPage(() => import('@/pages/ReportsPage'), 'ReportsPage')}
          </RequireRole>
        ),
      },
      {
        path: 'devices',
        element: (
          <RequireRole roles={['super_admin']}>
            {lazyPage(() => import('@/pages/DevicesPage'), 'DevicesPage')}
          </RequireRole>
        ),
      },
      {
        path: 'devices/:id',
        element: (
          <RequireRole roles={['super_admin']}>
            {lazyPage(() => import('@/pages/DeviceDetailPage'), 'DeviceDetailPage')}
          </RequireRole>
        ),
      },
      { path: 'employees', element: lazyPage(() => import('@/pages/TerminalUsersPage'), 'TerminalUsersPage') },
      { path: 'employees/:id', element: lazyPage(() => import('@/pages/EmployeeProfilePage'), 'EmployeeProfilePage') },
      { path: 'entries-exits', element: lazyPage(() => import('@/pages/EntriesExitsPage'), 'EntriesExitsPage') },
      {
        path: 'users',
        element: (
          <RequireRole roles={['super_admin']}>
            {lazyPage(() => import('@/pages/WebUsersPage'), 'WebUsersPage')}
          </RequireRole>
        ),
      },
      {
        path: 'settings/branding',
        element: (
          <RequireRole roles={['super_admin']}>
            {lazyPage(() => import('@/pages/BrandingPage'), 'BrandingPage')}
          </RequireRole>
        ),
      },
      {
        path: 'settings/schedule',
        element: (
          <RequireRole roles={['admin']}>
            {lazyPage(() => import('@/pages/SchedulePage'), 'SchedulePage')}
          </RequireRole>
        ),
      },
      {
        path: 'settings/holidays',
        element: (
          <RequireRole roles={['admin', 'super_admin']}>
            {lazyPage(() => import('@/pages/HolidaysPage'), 'HolidaysPage')}
          </RequireRole>
        ),
      },
      {
        path: 'settings/time-off',
        element: (
          <RequireRole roles={['admin', 'super_admin']}>
            {lazyPage(() => import('@/pages/TimeOffPage'), 'TimeOffPage')}
          </RequireRole>
        ),
      },
      {
        path: 'settings/time-off/calc',
        element: (
          <RequireRole roles={['admin', 'super_admin']}>
            {lazyPage(() => import('@/pages/VacationCalcPage'), 'VacationCalcPage')}
          </RequireRole>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
